<script setup lang="ts">
import { computed } from 'vue';

import arrowLongDownIcon from '@/assets/icons/arrow-long-down.svg';
import arrowLongUpIcon from '@/assets/icons/arrow-long-up.svg';
import { formatTimestampRelativeOrUTC, formatTokenAmount } from '@/helpers/formatters';
import { AmountMode } from '@/references/axios/transactions/types';

import UiIcon from '@/components/ui/UiIcon.vue';
import UiSkeleton from '@/components/ui/UiSkeleton.vue';
import UiText from '@/components/ui/UiText.vue';

interface Props {
  timestamp: number;
  price: string;
  volatility: number;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false
});

const isPositive = computed((): boolean => props.volatility >= 0);

const formattedVolatilityValue = computed(
  () => `${formatTokenAmount(Math.abs(props.volatility), 2, '', AmountMode.ValueOnly, false, 2)}%`
);

const time = computed((): string => formatTimestampRelativeOrUTC(props.timestamp, 'DD MMMM YYYY'));
</script>

<template>
  <div class="price-group" :class="{ 'price-group_positive': isPositive }">
    <div class="price-group__body">
      <template v-if="props.isLoading">
        <UiSkeleton class="price-group__skeleton-item" width="64px" height="16px" />
        <UiSkeleton class="price-group__skeleton-item" width="56px" height="16px" />
        <UiSkeleton class="price-group__skeleton-item" width="48px" height="16px" />
      </template>
      <template v-else>
        <UiText class="price-group__value" weight="semibold" tag="p">{{ props.price }}</UiText>
        <UiText class="price-group__value price-group__value_with-icon" weight="semibold" tag="p">
          <UiIcon
            class="price-group__icon"
            :name="isPositive ? arrowLongUpIcon : arrowLongDownIcon"
            :size="16"
          />
          {{ formattedVolatilityValue }}
        </UiText>
        <UiText class="price-group__value" weight="semibold" tag="p">{{ time }}</UiText>
      </template>
    </div>
  </div>
</template>

<style scoped>
.price-group {
  color: var(--color-secondary-text);
}

.price-group_positive {
  color: var(--color-green);
}

.price-group__body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: -2px -8px;
}

.price-group__skeleton-item {
  margin: 6px 8px;
}

.price-group__value {
  margin: 2px 8px;
}

.price-group__value_with-icon {
  display: flex;
  align-items: center;
}

.price-group__icon {
  flex-shrink: 0;
  margin: 0 2px 0 0;
}
</style>
